import { useState, useEffect } from 'react';
import { useAuth } from '../state/useAuth';
import { useData } from '../context/DataContext';
import WorkerPlanning from '../pages/worker/tabs/WorkerPlanning';
import WorkerChantier from '../pages/worker/tabs/WorkerChantier';
import WorkerDocuments from '../pages/worker/tabs/WorkerDocuments';
import WorkerProfil from '../pages/worker/tabs/WorkerProfil';
import './WorkerLayout.css';

const TABS = [
  {
    id: 'planning',
    label: 'Planning',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    )
  },
  {
    id: 'chantier',
    label: 'Chantier',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    )
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
      </svg>
    )
  },
  {
    id: 'profil',
    label: 'Profil',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    )
  }
];

export default function WorkerLayout() {
  const { model: user } = useAuth();
  const { assignments, loading, refresh } = useData();
  const [activeTab, setActiveTab] = useState('planning');
  const [selectedChantierId, setSelectedChantierId] = useState(null);

  // Sync activeChantier from real-time assignments
  const activeChantier = assignments.find(a =>
    a.chantier === selectedChantierId && a.user === user?.id
  )?.expand?.chantier || (assignments.find(a => a.expand?.chantier?.id === selectedChantierId)?.expand?.chantier);

  const activeAssignment = assignments.find(a =>
    (a.chantier === selectedChantierId || a.expand?.chantier?.id === selectedChantierId) && a.user === user?.id
  );

  // Consolidate current chantier data
  const currentChantierData = activeChantier ? {
    ...activeChantier,
    assignmentNotes: activeAssignment?.notes,
    assignmentId: activeAssignment?.id,
    assignmentStart: activeAssignment?.start_date,
    assignmentEnd: activeAssignment?.end_date,
    assignmentHours: activeAssignment?.hours_override
  } : null;

  // Auto-select today's mission if none selected
  useEffect(() => {
    if (!selectedChantierId && !loading && assignments && user) {
      const today = new Date().toDateString();
      const todayAssignment = assignments.find(a =>
        a.user === user.id &&
        new Date(a.start_date).toDateString() === today
      );
      if (todayAssignment) setSelectedChantierId(todayAssignment.chantier);
    }
  }, [assignments, loading, user, selectedChantierId]);

  // Listen to external open events (from Dashboard)
  useEffect(() => {
    const handleOpen = (e) => {
      const mission = e.detail;
      const chantierId = mission.chantier || mission.expand?.chantier?.id;
      if (chantierId) {
        setSelectedChantierId(chantierId);
        setActiveTab('chantier');
      }
    };
    window.addEventListener('open-mission', handleOpen);
    return () => window.removeEventListener('open-mission', handleOpen);
  }, []);

  const openChantier = (chantier) => {
    setSelectedChantierId(chantier.id || chantier);
    setActiveTab('chantier');
  };

  return (
    <div className="worker-layout">
      <header className="worker-header">
        <div className="w-header-left">
          <span className="w-header-name">Bonjour, {user?.name?.split(' ')[0]}</span>
        </div>
        <div className="w-header-right">
          <div className="w-live-indicator">
            <span className="w-pulse" />
            <span className="w-live-text">DIRECT</span>
          </div>
        </div>
      </header>

      <div className="worker-content">
        {loading && activeTab === 'planning' ? (
          <div className="w-loading-overlay">Chargement...</div>
        ) : (
          <>
            {activeTab === 'planning' && <WorkerPlanning onOpenChantier={openChantier} />}
            {activeTab === 'chantier' && <WorkerChantier chantier={currentChantierData} onClose={() => setSelectedChantierId(null)} />}
            {activeTab === 'documents' && <WorkerDocuments />}
            {activeTab === 'profil' && <WorkerProfil />}
          </>
        )}
      </div>

      <nav className="worker-bottom-nav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`w-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            aria-label={tab.label}
          >
            {tab.icon(activeTab === tab.id)}
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
